import * as Sentry from '@sentry/browser';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { ILocalizacao, IUsuario } from '../modelo';
import LogusService from '../services/LogusService';
import Configuracao from '../utils/Configuracao';
import StorageUtil from '../utils/StorageUtils';
import GabineteStore from './GabineteStore';
import StorePrimaria from './StoreParaSingleton';

class UsuarioStore extends StorePrimaria {
    usuario: IUsuario;
    localizacao: ILocalizacao;
    visualizarProcessos = true;
    token: string = '';
    processosFavoritosUsuario: boolean[]; //map<number, boolean>

    constructor() {
        // TODO: [mobx-undecorate] verify the constructor arguments and the arguments of this automatically generated super call
        super();

        makeObservable(this, {
            usuario: observable,
            localizacao: observable,
            visualizarProcessos: observable,
            token: observable,
            processosFavoritosUsuario: observable,
            objetoSave: computed,
            inicializar: action,
            configurarUsuario: action,
            isUsuarioLogado: computed,
            localizacaoList: computed,
            ehMagistrado: computed,
            ehSecretario: computed,
            ehLocalizacaoSecretaria: computed,
            handleToggleVisualizarProcessos: action,
            limpar: action
        });
    }

    get objetoSave() {
        return {
            nomeStore: 'UsuarioStore',
            usuario: this.usuario,
            localizacao: this.localizacao,
            visualizarProcessos: this.visualizarProcessos
        };
    }


    inicializar = () => {
        if (this.objetoSave) {
            const storage = StorageUtil.getObject(this.nomeStorage);
            if (storage) {
                for (const key in this.objetoSave) {
                    this[key] = storage[key];
                }
            }
        }
    };

    async configurarUsuario(token) {
        try {
            if (token) {
                this.token = token;
                await this.recuperarDetalhesUsuarioLogado(token);
                return !!this.isUsuarioLogado;
            }
        } catch (e) {
            console.error(e);
        }
        return false;
    }
    // administrador magistrado servidor

    async recuperarDetalhesUsuarioLogado(token) {
        const infoToken: any = (await LogusService.traduzToken(token)).data;
        const siglaGabinete = (infoToken?.localizacoes?.[0]?.sigla || '').trim();

        const url = window.location.href;
        if (url.includes('.bet.') || url.includes('.prd.')) {
            const gabineteAtual: any = this.gabinete;
            Sentry.setUser({
                id: infoToken.login, // `${Ambiente.appVersao},`,
                username: infoToken.cpf,
                localizacao: gabineteAtual,
                other: {
                    nome: infoToken.nome,
                    roles: infoToken.roles
                }
            } as any);
        }

        const lista = GabineteStore.listaUsuariosGabinete || [];

        const usuario: IUsuario = {
            login: infoToken.login,
            token: token,
            nome: '',
            usuarioLocalizacaoList: [{
                usuarioLocalizacaoMagistradoServidor: {
                    orgaoJulgador: {
                        sigla: siglaGabinete
                    }
                },
                papel: infoToken.roles
            }],
            localizacao: {
                papel: infoToken.roles,
                siglaOrgaoJulgador: siglaGabinete,
            }
        };

        runInAction(() => {
            this.usuario = usuario;
            this.usuario = { ...this.usuario, nome: GabineteStore.recuperarNomeServidor(infoToken.cpf, lista, infoToken.nome) };
            GabineteStore.limparCacheNomes();
            this.token = token;
            this.localizacao = (usuario as any).localizacao;
        });

        // Inicia o carregamento da descrição do gabinete o mais cedo possível,
        // antes mesmo de TelaPrincipal montar, para evitar flash de título vazio.
        GabineteStore.carregarDescricaoGabineteAtual(siglaGabinete).catch(() => {});
    }

    tokenValido = (token: string) => {
        try {
            if (!token || typeof token !== 'string' || !token.includes('.')) {
                return false;
            }

            const parts = token.split('.');
            if (parts.length < 2) {
                return false;
            }

            const payload: any = JSON.parse(atob(parts[1]));

            if (payload?.validade?.date && payload?.validade?.time) {
                const date = payload.validade.date;
                const time = payload.validade.time;
                const dataValidade = new Date(
                    `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}T${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}:${String(time.second).padStart(2, '0')}`
                );
                return dataValidade > new Date();
            }

            if (typeof payload?.exp === 'number') {
                return payload.exp * 1000 > Date.now();
            }

            return true;
        } catch (e) {
            console.warn('Não foi possível validar o token; mantendo sessão ativa.', e);
            return true;
        }
    }

    get login() {
        return this.usuario && this.usuario.login;
    }

    @computed get gabinete() {
        return this.localizacao && this.localizacao.siglaOrgaoJulgador;
    }

    get isUsuarioLogado() {
        // Em hard refresh (Ctrl+F5), usuario pode vir do storage com token antigo.
        // Consideramos logado apenas quando o token da sessao atual foi configurado.
        return !!this.token;
    }

    isSuperAdministrador = () => {
        return this.usuarioTemPapel(Configuracao.SUPER_ADMINISTRADOR);
    }

    get localizacaoList() {
        return this.usuario && this.usuario.usuarioLocalizacaoList ? this.usuario.usuarioLocalizacaoList : [];
    }

    usuarioTemPapel = (papel) => {
        return this.localizacaoList.some((localizacao) => {
            return localizacao.usuarioLocalizacaoMagistradoServidor.orgaoJulgador.sigla === this.gabinete && localizacao.papel.find(p => papel && p && papel.toLocaleUpperCase() === (p.perfil ? p.perfil.toLocaleUpperCase() : p.toLocaleUpperCase()))
        });
    }

    get ehMagistrado() {
        return this.usuarioTemPapel(Configuracao.PERFIL_MAGISTRADO);
    }

    get ehSecretario() {
        return this.usuarioTemPapel(Configuracao.PERFIL_SECRETARIO);
    }

    get ehLocalizacaoSecretaria() {
        const sigla = (this.gabinete || this.localizacao?.siglaOrgaoJulgador || (this.localizacao as any)?.localizacao || '').trim().toUpperCase();
        return /^SETR/i.test(sigla) || sigla === 'SETPOESDC' || sigla === 'SEPREX';
    }

    handleToggleVisualizarProcessos = () => {
        this.visualizarProcessos = !this.visualizarProcessos;
    };

    limpar = () => {
        this.delete();
        this.usuario = undefined;
        this.localizacao = undefined;
        this.token = '';
    };

    configToken = () => {
        return new Promise((resolve, _reject) => {
            if (this.token || (this.usuario && this.usuario.token)) {
                resolve(this.token || this.usuario.token);
            } else {
                setTimeout(async () => {
                    resolve(await this.configToken());
                }, 200);
            }
        })
    }
}

export default new UsuarioStore();
